/**
 * <certificate-text> - fold-ud af certifikat-teksten i certificates.liquid.
 *
 * Progressive enhancement: serveren rendrer den fulde tekst, linket og en
 * skjult knap. Uden JS (eller hvis modulet fejler) staar alt fremme. Med JS
 * klippes teksten til 3 linjer (CSS paa [data-state='collapsed']), og knappen
 * vises KUN hvis teksten faktisk flyder over - en kort tekst faar ingen knap
 * og viser sit link direkte.
 *
 * Linjeantallet afhaenger af flisens bredde og af hvornaar webfonten lander,
 * saa overflow maales igen naar BREDDEN aendrer sig og efter
 * document.fonts.ready - men kun saa laenge brugeren ikke selv har foldet
 * teksten ud. Maalingen koeres i en rAF uden for ResizeObserver-callbacken,
 * fordi den selv aendrer elementets hoejde (og via subgriddet naboernes).
 *
 * Sektionen ligger langt under folden, saa den ene reflow ved init sker uden
 * for viewporten (ingen CLS). Elementet reconnectes selv ved
 * shopify:section:load; al tilstand nulstilles i connectedCallback.
 */
class CertificateText extends HTMLElement {
  connectedCallback() {
    this.text = this.querySelector('.certificates__text');
    this.toggle = this.querySelector('.certificates__toggle');
    if (!this.text || !this.toggle) return;

    this.expanded = false;
    this.toggle.setAttribute('aria-expanded', 'false');
    this.onToggle = () => this.#setExpanded(!this.expanded);
    this.toggle.addEventListener('click', this.onToggle);

    this.#measure();
    this.width = this.offsetWidth;
    this.ro = new ResizeObserver(() => {
      if (this.offsetWidth === this.width) return;
      this.width = this.offsetWidth;
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.#measure());
    });
    this.ro.observe(this);
    document.fonts?.ready.then(() => {
      if (this.isConnected) this.#measure();
    });
  }

  disconnectedCallback() {
    this.ro?.disconnect();
    cancelAnimationFrame(this.frame);
    this.toggle?.removeEventListener('click', this.onToggle);
  }

  #measure() {
    if (this.expanded) return;
    this.dataset.state = 'collapsed';
    const overflows = this.text.scrollHeight > this.text.clientHeight + 1;
    this.toggle.hidden = !overflows;
    if (!overflows) delete this.dataset.state;
  }

  #setExpanded(expanded) {
    this.expanded = expanded;
    this.toggle.setAttribute('aria-expanded', String(expanded));
    if (expanded) {
      delete this.dataset.state;
      return;
    }
    this.#measure();
    // Blev flisen bredere mens den var foldet ud, kan teksten nu passe, og
    // knappen skjules med fokus paa sig. Flyt fokus, saa det ikke tabes.
    if (this.toggle.hidden) {
      const link = this.querySelector('.certificates__link');
      if (link) {
        link.focus();
      } else {
        this.text.tabIndex = -1;
        this.text.focus();
      }
    }
  }
}

if (!customElements.get('certificate-text')) {
  customElements.define('certificate-text', CertificateText);
}
